import type { Role } from '../hooks/useValidateSession';

export interface InvitePositionOption {
    value: Role;
    label: string;
}

const BASE_INVITE_POSITION_OPTIONS: InvitePositionOption[] = [
    { value: 'executive', label: 'Executive' },
    { value: 'central-parts', label: 'Central Parts' },
    { value: 'parts-manager', label: 'Parts Manager' },
    { value: 'gm', label: 'General Manager' },
    { value: 'estimator', label: 'Estimator' },
    { value: 'it', label: 'IT' },
];

const INVOICING_OPTION: InvitePositionOption = {
    value: 'invoicing',
    label: 'Invoicing',
};

const TECHNICIAN_OPTION: InvitePositionOption = {
    value: 'technician',
    label: 'Technician',
};

/** Display label for every role, including ones that are not inviteable. */
export const POSITION_LABELS: Record<Role, string> = {
    'central-parts': 'Central Parts',
    'parts-manager': 'Parts Manager',
    estimator: 'Estimator',
    executive: 'Executive',
    gm: 'General Manager',
    invoicing: 'Invoicing',
    it: 'IT',
    other: 'Other',
    technician: 'Technician',
};

/**
 * Label for a user's role — the same string the Users table shows in Position.
 * Roles outside this map (the Yaxle team mention) keep `fallback`.
 */
export function positionLabel(role: string | null | undefined, fallback?: string): string {
    if (role && Object.prototype.hasOwnProperty.call(POSITION_LABELS, role)) {
        return POSITION_LABELS[role as Role];
    }
    return fallback ?? role ?? '';
}

const OTHER_OPTION: InvitePositionOption = {
    value: 'other',
    label: 'Other',
};

/** Inventory-only orgs offer just these positions. */
const INVENTORY_ORG_POSITION_OPTIONS: InvitePositionOption[] = [
    { value: 'executive', label: 'Executive' },
    { value: 'gm', label: 'General Manager' },
    { value: 'it', label: 'IT' },
    TECHNICIAN_OPTION,
];

/**
 * Inviteable position options for UI selects and Excel template dropdowns.
 * - Invoicing is included only when the org has NEX invoicing set up (`hasInvoicingReporting`).
 * - Technician is included only when inventory is enabled for the org.
 * - Inventory-only orgs (`inventoryOrg`) get a fixed list and ignore the other flags.
 */
export function getInvitePositionOptions(opts?: {
    includeTechnician?: boolean;
    includeInvoicing?: boolean;
    inventoryOrg?: boolean;
}): InvitePositionOption[] {
    if (opts?.inventoryOrg === true) return [...INVENTORY_ORG_POSITION_OPTIONS];
    const includeTechnician = opts?.includeTechnician === true;
    const includeInvoicing = opts?.includeInvoicing === true;

    const options = [...BASE_INVITE_POSITION_OPTIONS];
    if (includeInvoicing) options.push(INVOICING_OPTION);
    if (includeTechnician) options.push(TECHNICIAN_OPTION);
    options.push(OTHER_OPTION);
    return options;
}

/** Map common label / slug variants to Role for template upload parsing. */
export function buildInvitePositionMap(
    options: InvitePositionOption[],
): Record<string, Role> {
    const map: Record<string, Role> = {};
    for (const opt of options) {
        map[opt.value] = opt.value;
        map[opt.label.toLowerCase()] = opt.value;
    }
    // Extra aliases, only for positions that are offered
    const offered = new Set(options.map((opt) => opt.value));
    const aliases: Record<string, Role> = {
        'central parts': 'central-parts',
        'parts manager': 'parts-manager',
        'general manager': 'gm',
    };
    for (const [alias, role] of Object.entries(aliases)) {
        if (offered.has(role)) map[alias] = role;
    }
    return map;
}
